import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthStore } from '../stores/authStore';
import { useBusinessPlan } from '../hooks/useBusinessPlan';
import { COLLECTIONS, SUBCOLLECTIONS } from '../lib/collections';
import AnimatedPage from '../components/shared/AnimatedPage';
import LoadingScreen from '../components/shared/LoadingScreen';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  cost: number;
  category: string;
  stock: number;
  createdAt: unknown;
}

const CATEGORIES = ['Physical', 'Digital', 'Service', 'Food & Drink', 'Craft', 'Other'];

const emptyForm = { name: '', description: '', price: '', cost: '', category: 'Physical', stock: '' };

export default function ProductCataloguePage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const { plan, loading } = useBusinessPlan(planId);

  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const productsPath = user && profile && planId
    ? `${COLLECTIONS.USERS}/${user.uid}/${SUBCOLLECTIONS.PROFILES}/${profile.id}/${SUBCOLLECTIONS.BUSINESSES}/${planId}/products`
    : null;

  useEffect(() => {
    if (!productsPath) return;
    const ref = collection(db, productsPath);
    const unsub = onSnapshot(ref, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Product));
      items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProducts(items);
      setLoadingProducts(false);
    }, () => setLoadingProducts(false));
    return unsub;
  }, [productsPath]);

  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 2500);
  }, []);

  const handleSave = async () => {
    if (!productsPath || !form.name.trim()) return;
    setSaving(true);
    try {
      const id = editId || doc(collection(db, productsPath)).id;
      const ref = doc(db, productsPath, id);
      await setDoc(ref, {
        name: form.name.trim(),
        description: form.description.trim(),
        price: parseFloat(form.price) || 0,
        cost: parseFloat(form.cost) || 0,
        category: form.category,
        stock: parseInt(form.stock, 10) || 0,
        ...(editId ? {} : { createdAt: serverTimestamp() }),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      toast(editId ? 'Product updated!' : 'Product added!');
      resetForm();
    } catch (e) {
      console.error(e);
      toast('Failed to save product.');
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!productsPath) return;
    try {
      await deleteDoc(doc(db, productsPath, id));
      toast('Product deleted.');
    } catch (e) {
      console.error(e);
      toast('Failed to delete.');
    }
  };

  const handleEdit = (p: Product) => {
    setEditId(p.id);
    setForm({
      name: p.name,
      description: p.description,
      price: String(p.price),
      cost: String(p.cost),
      category: p.category,
      stock: String(p.stock),
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditId(null);
    setShowForm(false);
  };

  if (loading) return <LoadingScreen />;
  if (!plan) return (
    <AnimatedPage><div className="flex min-h-screen items-center justify-center"><p className="text-gray-500">Plan not found</p></div></AnimatedPage>
  );

  const totalProducts = products.length;
  const totalValue = products.reduce((s, p) => s + p.price * p.stock, 0);
  const avgMargin = totalProducts > 0
    ? products.reduce((s, p) => s + (p.price > 0 ? ((p.price - p.cost) / p.price) * 100 : 0), 0) / totalProducts
    : 0;

  return (
    <AnimatedPage>
      <div className="min-h-screen bg-gray-50 px-4 pb-12 pt-6">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6 flex items-center justify-between">
            <button onClick={() => navigate(`/hq/${planId}`)} className="text-sm font-medium text-brand-500 hover:text-brand-600">← Back to HQ</button>
            <h1 className="text-lg font-bold text-gray-900">Product Catalogue</h1>
            <button onClick={() => { resetForm(); setShowForm(true); }} className="min-h-[44px] rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">+ Add Product</button>
          </div>

          {/* Stats */}
          <div className="mb-6 grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{totalProducts}</p>
              <p className="text-xs text-gray-500">Products</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">${totalValue.toFixed(0)}</p>
              <p className="text-xs text-gray-500">Inventory Value</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{avgMargin.toFixed(0)}%</p>
              <p className="text-xs text-gray-500">Avg Margin</p>
            </div>
          </div>

          {/* Add/Edit Form */}
          <AnimatePresence>
            {showForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6 overflow-hidden"
              >
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h2 className="mb-4 text-sm font-bold text-gray-900">{editId ? 'Edit Product' : 'New Product'}</h2>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">Name *</label>
                      <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" placeholder="Product name" />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">Category</label>
                      <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none">
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-gray-600">Description</label>
                      <textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" placeholder="What is this product?" />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">Selling Price ($)</label>
                      <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">Cost to Make ($)</label>
                      <input type="number" min="0" step="0.01" value={form.cost} onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600">Stock</label>
                      <input type="number" min="0" step="1" value={form.stock} onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" placeholder="0" />
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button onClick={handleSave} disabled={saving || !form.name.trim()} className="min-h-[44px] rounded-xl bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">{saving ? 'Saving…' : editId ? 'Update' : 'Add Product'}</button>
                    <button onClick={resetForm} className="min-h-[44px] rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Product Grid */}
          {loadingProducts ? (
            <div className="py-12 text-center text-gray-400">Loading products…</div>
          ) : products.length === 0 ? (
            <div className="py-16 text-center">
              <span className="text-5xl">📦</span>
              <h2 className="mt-4 text-xl font-bold text-gray-900">No products yet</h2>
              <p className="mt-2 text-sm text-gray-500">Add your first product to start building your catalogue!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((p) => {
                const margin = p.price > 0 ? ((p.price - p.cost) / p.price) * 100 : 0;
                return (
                  <motion.div
                    key={p.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="rounded-xl border border-gray-200 bg-white p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900">{p.name}</h3>
                        <span className="mt-0.5 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">{p.category}</span>
                      </div>
                      <p className="text-lg font-bold text-brand-600">${p.price.toFixed(2)}</p>
                    </div>
                    {p.description && <p className="mt-2 text-xs text-gray-500 line-clamp-2">{p.description}</p>}
                    <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
                      <span>Cost: ${p.cost.toFixed(2)}</span>
                      <span>Margin: {margin.toFixed(0)}%</span>
                      <span>Stock: {p.stock}</span>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => handleEdit(p)} className="min-h-[36px] flex-1 rounded-lg border border-gray-200 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50">Edit</button>
                      <button onClick={() => handleDelete(p.id)} className="min-h-[36px] flex-1 rounded-lg border border-red-200 py-1 text-xs font-medium text-red-500 hover:bg-red-50">Delete</button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {toastMsg && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-6 py-3 text-sm font-medium text-white shadow-lg">{toastMsg}</motion.div>
        )}
      </div>
    </AnimatedPage>
  );
}
